import { subscribeTenantChange, useTenantStore } from "@/store/tenant.store.js";
import { useAuthStore } from "@/store/auth.store.js";
import {
  getTenantRolePermissionSnapshot,
  tenantSnapshotHasAnyPagePermissionOr,
} from "@/lib/tenant-role-permissions.js";
import { clearDomainDropdownCaches } from "@/lib/services/clear-domain-dropdown-caches.js";
import {
  notificationIntegration,
  controlIntegration,
  formsIntegration,
  riskRegisterIntegration,
  formTaskIntegration,
  kvkkCompanyIntegration,
  userTrainingIntegration,
} from "@/lib/api/integrations-registry.js";

function isZustandStoreHook(value) {
  return typeof value === "function" && typeof value.getState === "function";
}

function shouldAutoRefetchMethodName(methodName) {
  if (!methodName || typeof methodName !== "string") return false;
  if (!methodName.startsWith("fetch")) return false;
  // Avoid methods that usually require an argument
  if (methodName.includes("ById")) return false;
  return true;
}

/**
 * Methods that require route/context (entity id, user id) and must not be
 * called argumansız on tenant change — would cause 400 or deprecation warnings.
 * Key: store file name (e.g. "kpi.store.js"), value: method names to skip.
 */
const REFETCH_EXCLUDE_BY_STORE = {
  "system-forms.store.js": ["fetchSurveys"],
  "kpi.store.js": ["fetchRelatedKpis"],
  "strategic-initiative.store.js": ["fetchRelatedInitiatives", "fetchGanttData"],
  "risk-reports.store.js": ["fetchReports"],
  /* Pages already refetch these when selectedTenantId changes; global call hits optional :5000 APIs or duplicates work */
  "home.store.js": ["fetchLayout", "fetchHomeData"],
  "incidentMonitoring.store.js": ["fetchLayout", "fetchData"],
  "riskMonitoring.store.js": ["fetchLayout", "fetchData"],
  /* Require document id — blind refetch produced /document/undefined/revisions */
  "document-revision.store.js": ["fetchRevisions", "fetchActiveRevision"],
  /* Avoid 401 noise while access token is refreshing after tenant switch */
  "notification-preference.store.js": ["fetchPreferences"],
  "company.store.js": ["fetchCompanyInfo"],
};

/**
 * When set, tenant refetch runs only if the user has hasAny() on at least one listed page
 * (same rules as usePermission). Omit a store to keep legacy “always refetch” behavior.
 * Extend when new stores gain fetch* actions that should not run without module access.
 */
const REFETCH_PAGE_KEYS_BY_STORE = {
  "form-task.store.js": ["myWork"],
  "kvkk-company.store.js": ["definitionsKvkkCompany"],
  "kvkk-category.store.js": ["definitionsKvkkCategory"],
  "collection-method.store.js": ["definitionsKvkkCollectionMethod"],
  "subject-person-group.store.js": ["definitionsKvkkSubjectPersonGroup"],
  "processing-purpose.store.js": ["definitionsKvkkProcessingPurpose"],
  "legal-reason.store.js": ["definitionsKvkkLegalReason"],
  "application-method.store.js": ["definitionsApplicationTrackingApplicationMethod"],
  "application-tracking.store.js": [
    "definitionsApplicationTrackingApplicationMethod",
    "definitionsKvkkSubjectPersonGroup",
    "definitionsApplicationTrackingApplicationRequestInformation",
    "gdprApplicationTracking",
  ],
  "ticket.store.js": ["ticketing"],
  "ticket-template.store.js": ["ticketing"],

  /* ISO / iç kontrol / risk işlemleri — tenant refetch yoksa 403 gürültüsü */
  "my-training.store.js": ["isoMyTraining"],
  "control.store.js": ["internalControlControl"],
  "risk-register.store.js": ["riskOperationsRiskRegister"],
  "risk-treatment.store.js": ["riskOperationsTreatment"],
  "matrix-configuration.store.js": ["definitionsScaleRiskAssessmentMatrix", "definitionsScaleCiaMatrix"],
  "bia-impact-configuration.store.js": ["definitionsScaleBiaImpact", "bcmBia"],
  "bia.store.js": ["bcmBia"],
  "asset-category.store.js": ["assetInventoryAssetCategory"],
  "assets.store.js": ["assetInventoryAssets"],
  "asset-group.store.js": ["assetInventoryAssetGroup"],

  /* Kontrol listesi + risk/varlık ilişkileri (aynı API ailesi) */
  "risk-related-assets.store.js": ["riskOperationsRiskRegister", "assetInventoryAssets"],
  "risk-existing-controls.store.js": ["riskOperationsRiskRegister", "internalControlControl"],

  /**
   * Home: fetchHomeData / fetchLayout REFETCH_EXCLUDE içinde — tenant refetch bunları çağırmaz.
   * Ana sayfa verisi tenant değişiminde home.jsx üzerinden gelir; buradaki anahtar exclude kalkarsa kullanılır.
   */
  "home.store.js": ["home"],
  "kpi.store.js": ["auditComplianceKpiDashboard"],
  "strategic-initiative.store.js": ["isoObjective"],

  /** Report ops forms (/report-operations/forms) */
  "forms.store.js": ["reportForms"],
};

function getStoreFileName(storePath) {
  const normalized = storePath.replace(/^.*[/\\]/, "");
  return normalized;
}

function refetchStore(storePath, storeHook, permissionSnapshot) {
  const storeFileName = getStoreFileName(storePath);
  const pageGate = REFETCH_PAGE_KEYS_BY_STORE[storeFileName];
  if (
    pageGate !== undefined &&
    !tenantSnapshotHasAnyPagePermissionOr(permissionSnapshot, pageGate)
  ) {
    return;
  }

  const excludedMethods = new Set(REFETCH_EXCLUDE_BY_STORE[storeFileName] || []);

  try {
    const state = storeHook.getState();
    if (!state || typeof state !== "object") return;

    Object.entries(state).forEach(([key, value]) => {
      if (excludedMethods.has(key)) return;
      if (!shouldAutoRefetchMethodName(key)) return;
      if (typeof value !== "function") return;

      try {
        value();
      } catch {
        // ignore
      }
    });
  } catch {
    // ignore
  }
}

function refetchIntegrationLists(permissionSnapshot) {
  try {
    notificationIntegration.fetchNotifications(1, false);
    notificationIntegration.fetchNotificationList({ page: 1 });
  } catch {
    // ignore
  }

  if (tenantSnapshotHasAnyPagePermissionOr(permissionSnapshot, ["internalControlControl"])) {
    try {
      controlIntegration.fetchControls();
    } catch {
      // ignore
    }
  }

  if (tenantSnapshotHasAnyPagePermissionOr(permissionSnapshot, ["reportForms"])) {
    try {
      formsIntegration.fetchForms();
    } catch {
      // ignore
    }
  }

  if (tenantSnapshotHasAnyPagePermissionOr(permissionSnapshot, ["riskOperationsRiskRegister"])) {
    try {
      riskRegisterIntegration.fetchRiskRegisters();
    } catch {
      // ignore
    }
  }

  if (tenantSnapshotHasAnyPagePermissionOr(permissionSnapshot, ["myWork"])) {
    try {
      formTaskIntegration.fetchMyWorkInbox({ page: 1, pageSize: 15 });
      formTaskIntegration.fetchWaitingTasks({ page: 1, pageSize: 20 });
      formTaskIntegration.fetchTasks();
      formTaskIntegration.fetchMyTasks();
    } catch {
      // ignore
    }
  }

  if (tenantSnapshotHasAnyPagePermissionOr(permissionSnapshot, ["definitionsKvkkCompany"])) {
    try {
      kvkkCompanyIntegration.getCompanyInfo();
      kvkkCompanyIntegration.fetchUsers();
    } catch {
      // ignore
    }
  }

  if (tenantSnapshotHasAnyPagePermissionOr(permissionSnapshot, ["isoMyTraining"])) {
    try {
      userTrainingIntegration.fetchMyTrainings();
    } catch {
      // ignore
    }
  }
}

const storeModules = import.meta.glob(["./store/*.store.js"], {
  eager: true,
});

const allStores = [];
Object.entries(storeModules).forEach(([path, mod]) => {
  if (!mod || typeof mod !== "object") return;
  Object.values(mod).forEach((exported) => {
    if (isZustandStoreHook(exported)) allStores.push({ path, hook: exported });
  });
});

const globalKey = "__sgrc_tenant_refetch_unsubscribe__";

if (globalThis[globalKey]) {
  try {
    globalThis[globalKey]();
  } catch {
    // ignore
  }
}

const TENANT_REFETCH_DEBOUNCE_MS = 120;
let tenantRefetchTimer = null;

globalThis[globalKey] = subscribeTenantChange(() => {
  // Immediate: drop stale dropdown mirrors so length-guards refetch for the new tenant.
  clearDomainDropdownCaches();

  if (tenantRefetchTimer) clearTimeout(tenantRefetchTimer);
  tenantRefetchTimer = setTimeout(() => {
    tenantRefetchTimer = null;
    const { token, isLoggingOut, user } = useAuthStore.getState();
    if (isLoggingOut || !token) return;
    const tenantId = useTenantStore.getState().selectedTenantId;
    const permissionSnapshot = getTenantRolePermissionSnapshot(user, tenantId);
    allStores.forEach(({ path, hook }) => refetchStore(path, hook, permissionSnapshot));
    refetchIntegrationLists(permissionSnapshot);
  }, TENANT_REFETCH_DEBOUNCE_MS);
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (tenantRefetchTimer) {
      clearTimeout(tenantRefetchTimer);
      tenantRefetchTimer = null;
    }
    try {
      globalThis[globalKey]?.();
    } catch {
      // ignore
    }
    globalThis[globalKey] = null;
  });
}
